/**
 * @preserve FastClick: polyfill to remove click delays on browsers with touch UIs.
 *
 * @version 1.0.1
 * @codingstandard ftlabs-jsv2
 * @copyright The Financial Times Limited [All Rights Reserved]
 * @license MIT License (see LICENSE.txt)
 */

/*jslint browser:true, node:true*/
/*global define, Event, Node*/


/**
 * Instantiate fast-clicking listeners on the specificed layer.
 *
 * @constructor
 * @param {Element} layer The layer to listen on
 * @param {Object} options The options to override the defaults
 */
function FastClick(layer, options) {
	'use strict';
	var oldOnClick;

	options = options || {};

	/**
	 * Whether a click is currently being tracked.
	 *
	 * @type boolean
	 */
	this.trackingClick = false;


	/**
	 * Timestamp for when click tracking started.
	 *
	 * @type number
	 */
	this.trackingClickStart = 0;


	/**
	 * The element being tracked for a click.
	 *
	 * @type EventTarget
	 */
	this.targetElement = null;


	/**
	 * X-coordinate of touch start event.
	 *
	 * @type number
	 */
	this.touchStartX = 0;


	/**
	 * Y-coordinate of touch start event.
	 *
	 * @type number
	 */
	this.touchStartY = 0;


	/**
	 * ID of the last touch, retrieved from Touch.identifier.
	 *
	 * @type number
	 */
	this.lastTouchIdentifier = 0;


	/**
	 * Touchmove boundary, beyond which a click will be cancelled.
	 *
	 * @type number
	 */
	this.touchBoundary = options.touchBoundary || 10;


	/**
	 * The FastClick layer.
	 *
	 * @type Element
	 */
	this.layer = layer;

	/**
	 * The minimum time between tap(touchstart and touchend) events
	 *
	 * @type number
	 */
	this.tapDelay = options.tapDelay || 200;

	if (FastClick.notNeeded(layer)) {
		return;
	}

	// Some old versions of Android don't have Function.prototype.bind
	function bind(method, context) {
		return function() { return method.apply(context, arguments); };
	}


	var methods = ['onMouse', 'onClick', 'onTouchStart', 'onTouchMove', 'onTouchEnd', 'onTouchCancel'];
	var context = this;
	for (var i = 0, l = methods.length; i < l; i++) {
		context[methods[i]] = bind(context[methods[i]], context);
	}

	// Set up event handlers as required
	if (deviceIsAndroid) {
		layer.addEventListener('mouseover', this.onMouse, true);
		layer.addEventListener('mousedown', this.onMouse, true);
		layer.addEventListener('mouseup', this.onMouse, true);
	}

	layer.addEventListener('click', this.onClick, true);
	layer.addEventListener('touchstart', this.onTouchStart, false);
	layer.addEventListener('touchmove', this.onTouchMove, false);
	layer.addEventListener('touchend', this.onTouchEnd, false);
	layer.addEventListener('touchcancel', this.onTouchCancel, false);

	// Hack is required for browsers that don't support Event#stopImmediatePropagation (e.g. Android 2)
	// which is how FastClick normally stops click events bubbling to callbacks registered on the FastClick
	// layer when they are cancelled.
	if (!Event.prototype.stopImmediatePropagation) {
		layer.removeEventListener = function(type, callback, capture) {
			var rmv = Node.prototype.removeEventListener;
			if (type === 'click') {
				rmv.call(layer, type, callback.hijacked || callback, capture);
			} else {
				rmv.call(layer, type, callback, capture);
			}
		};

		layer.addEventListener = function(type, callback, capture) {
			var adv = Node.prototype.addEventListener;
			if (type === 'click') {
				adv.call(layer, type, callback.hijacked || (callback.hijacked = function(event) {
					if (!event.propagationStopped) {
						callback(event);
					}
				}), capture);
			} else {
				adv.call(layer, type, callback, capture);
			}
		};
	}

	// If a handler is already declared in the element's onclick attribute, it will be fired before
	// FastClick's onClick handler. Fix this by pulling out the user-defined handler function and
	// adding it as listener.
	if (typeof layer.onclick === 'function') {

		// Android browser on at least 3.2 requires a new reference to the function in layer.onclick
		// - the old one won't work if passed to addEventListener directly.
		oldOnClick = layer.onclick;
		layer.addEventListener('click', function(event) {
			oldOnClick(event);
		}, false);
		layer.onclick = null;
	}
}


/**
 * Android requires exceptions.
 *
 * @type boolean
 */
var deviceIsAndroid = navigator.userAgent.indexOf('Android') > 0;


/**
 * iOS requires exceptions.
 *
 * @type boolean
 */
var deviceIsIOS = /iP(ad|hone|od)/.test(navigator.userAgent);


/**
 * iOS 4 requires an exception for select elements.
 *
 * @type boolean
 */
var deviceIsIOS4 = deviceIsIOS && (/OS 4_\d(_\d)?/).test(navigator.userAgent);


/**
 * iOS 6.0(+?) requires the target element to be manually derived
 *
 * @type boolean
 */
var deviceIsIOSWithBadTarget = deviceIsIOS && (/OS ([6-9]|\d{2})_\d/).test(navigator.userAgent);


/**
 * Determine whether a given element requires a native click.
 *
 * @param {EventTarget|Element} target Target DOM element
 * @returns {boolean} Returns true if the element needs a native click
 */
FastClick.prototype.needsClick = function(target) {
	'use strict';
	switch (target.nodeName.toLowerCase()) {

	// Don't send a synthetic click to disabled inputs (issue #62)
	case 'button':
	case 'select':
	case 'textarea':
		if (target.disabled) {
			return true;
		}

		break;
	case 'input':

		// File inputs need real clicks on iOS 6 due to a browser bug (issue #68)
		if ((deviceIsIOS && target.type === 'file') || target.disabled) {
			return true;
		}

		break;
	case 'label':
	case 'video':
		return true;
	}

	return (/\bneedsclick\b/).test(target.className);
};


/**
 * Determine whether a given element requires a call to focus to simulate click into element.
 *
 * @param {EventTarget|Element} target Target DOM element
 * @returns {boolean} Returns true if the element requires a call to focus to simulate native click.
 */
FastClick.prototype.needsFocus = function(target) {
	'use strict';
	switch (target.nodeName.toLowerCase()) {
	case 'textarea':
		return true;
	case 'select':
		return !deviceIsAndroid;
	case 'input':
		switch (target.type) {
		case 'button':
		case 'checkbox':
		case 'file':
		case 'image':
		case 'radio':
		case 'submit':
			return false;
		}

		// No point in attempting to focus disabled inputs
		return !target.disabled && !target.readOnly;
	default:
		return (/\bneedsfocus\b/).test(target.className);
	}
};


/**
 * Send a click event to the specified element.
 *
 * @param {EventTarget|Element} targetElement
 * @param {Event} event
 */
FastClick.prototype.sendClick = function(targetElement, event) {
	'use strict';
	var clickEvent, touch;

	// On some Android devices activeElement needs to be blurred otherwise the synthetic click will have no effect (#24)
	if (document.activeElement && document.activeElement !== targetElement) {
		document.activeElement.blur();
	}

	touch = event.changedTouches[0];

	// Synthesise a click event, with an extra attribute so it can be tracked
	clickEvent = document.createEvent('MouseEvents');
	clickEvent.initMouseEvent(this.determineEventType(targetElement), true, true, window, 1, touch.screenX, touch.screenY, touch.clientX, touch.clientY, false, false, false, false, 0, null);
	clickEvent.forwardedTouchEvent = true;
	targetElement.dispatchEvent(clickEvent);
};

FastClick.prototype.determineEventType = function(targetElement) {
	'use strict';

	//Issue #159: Android Chrome Select Box does not open with a synthetic click event
	if (deviceIsAndroid && targetElement.tagName.toLowerCase() === 'select') {
		return 'mousedown';
	}

	return 'click';
};


/**
 * @param {EventTarget|Element} targetElement
 */
FastClick.prototype.focus = function(targetElement) {
	'use strict';
	var length;

	// Issue #160: on iOS 7, some input elements (e.g. date datetime) throw a vague TypeError on setSelectionRange. These elements don't have an integer value for the selectionStart and selectionEnd properties, but unfortunately that can't be used for detection because accessing the properties also throws a TypeError. Just check the type instead. Filed as Apple bug #15122724.
	if (deviceIsIOS && targetElement.setSelectionRange && targetElement.type.indexOf('date') !== 0 && targetElement.type !== 'time') {
		length = targetElement.value.length;
		targetElement.setSelectionRange(length, length);
	} else {
		targetElement.focus();
	}
};


/**
 * Check whether the given target element is a child of a scrollable layer and if so, set a flag on it.
 *
 * @param {EventTarget|Element} targetElement
 */
FastClick.prototype.updateScrollParent = function(targetElement) {
	'use strict';
	var scrollParent, parentElement;

	scrollParent = targetElement.fastClickScrollParent;

	// Attempt to discover whether the target element is contained within a scrollable layer. Re-check if the
	// target element was moved to another parent.
	if (!scrollParent || !scrollParent.contains(targetElement)) {
		parentElement = targetElement;
		do {
			if (parentElement.scrollHeight > parentElement.offsetHeight) {
				scrollParent = parentElement;
				targetElement.fastClickScrollParent = parentElement;
				break;
			}

			parentElement = parentElement.parentElement;
		} while (parentElement);
	}

	// Always update the scroll top tracker if possible.
	if (scrollParent) {
		scrollParent.fastClickLastScrollTop = scrollParent.scrollTop;
	}
};


/**
 * @param {EventTarget} targetElement
 * @returns {Element|EventTarget}
 */
FastClick.prototype.getTargetElementFromEventTarget = function(eventTarget) {
	'use strict';

	// On some older browsers (notably Safari on iOS 4.1 - see issue #56) the event target may be a text node.
	if (eventTarget.nodeType === Node.TEXT_NODE) {
		return eventTarget.parentNode;
	}

	return eventTarget;
};


/**
 * On touch start, record the position and scroll offset.
 *
 * @param {Event} event
 * @returns {boolean}
 */
FastClick.prototype.onTouchStart = function(event) {
	'use strict';
	var targetElement, touch, selection;

	// Ignore multiple touches, otherwise pinch-to-zoom is prevented if both fingers are on the FastClick element (issue #111).
	if (event.targetTouches.length > 1) {
		return true;
	}

	targetElement = this.getTargetElementFromEventTarget(event.target);
	touch = event.targetTouches[0];

	if (deviceIsIOS) {

		// Only trusted events will deselect text on iOS (issue #49)
		selection = window.getSelection();
		if (selection.rangeCount && !selection.isCollapsed) {
			return true;
		}

		if (!deviceIsIOS4) {

			// Weird things happen on iOS when an alert or confirm dialog is opened from a click event callback (issue #23):
			// when the user next taps anywhere else on the page, new touchstart and touchend events are dispatched
			// with the same identifier as the touch event that previously triggered the click that triggered the alert.
			// Sadly, there is an issue on iOS 4 that causes some normal touch events to have the same identifier as an
			// immediately preceeding touch event (issue #52), so this fix is unavailable on that platform.
			if (touch.identifier === this.lastTouchIdentifier) {
				event.preventDefault();
				return false;
			}

			this.lastTouchIdentifier = touch.identifier;

			// If the target element is a child of a scrollable layer (using -webkit-overflow-scrolling: touch) and:
			// 1) the user does a fling scroll on the scrollable layer
			// 2) the user stops the fling scroll with another tap
			// then the event.target of the last 'touchend' event will be the element that was under the user's finger
			// when the fling scroll was started, causing FastClick to send a click event to that layer - unless a check
			// is made to ensure that a parent layer was not scrolled before sending a synthetic click (issue #42).
			this.updateScrollParent(targetElement);
		}
	}

	this.trackingClick = true;
	this.trackingClickStart = event.timeStamp;
	this.targetElement = targetElement;

	this.touchStartX = touch.pageX;
	this.touchStartY = touch.pageY;

	// Prevent phantom clicks on fast double-tap (issue #36)
	if ((event.timeStamp - this.lastClickTime) < this.tapDelay) {
		event.preventDefault();
	}

	return true;
};


/**
 * Based on a touchmove event object, check whether the touch has moved past a boundary since it started.
 *
 * @param {Event} event
 * @returns {boolean}
 */
FastClick.prototype.touchHasMoved = function(event) {
	'use strict';
	var touch = event.changedTouches[0], boundary = this.touchBoundary;

	if (Math.abs(touch.pageX - this.touchStartX) > boundary || Math.abs(touch.pageY - this.touchStartY) > boundary) {
		return true;
	}

	return false;
};


/**
 * Update the last position.
 *
 * @param {Event} event
 * @returns {boolean}
 */
FastClick.prototype.onTouchMove = function(event) {
	'use strict';
	if (!this.trackingClick) {
		return true;
	}

	// If the touch has moved, cancel the click tracking
	if (this.targetElement !== this.getTargetElementFromEventTarget(event.target) || this.touchHasMoved(event)) {
		this.trackingClick = false;
		this.targetElement = null;
	}

	return true;
};


/**
 * Attempt to find the labelled control for the given label element.
 *
 * @param {EventTarget|HTMLLabelElement} labelElement
 * @returns {Element|null}
 */
FastClick.prototype.findControl = function(labelElement) {
	'use strict';

	// Fast path for newer browsers supporting the HTML5 control attribute
	if (labelElement.control !== undefined) {
		return labelElement.control;
	}

	// All browsers under test that support touch events also support the HTML5 htmlFor attribute
	if (labelElement.htmlFor) {
		return document.getElementById(labelElement.htmlFor);
	}

	// If no for attribute exists, attempt to retrieve the first labellable descendant element
	// the list of which is defined here: http://www.w3.org/TR/html5/forms.html#category-label
	return labelElement.querySelector('button, input:not([type=hidden]), keygen, meter, output, progress, select, textarea');
};


/**
 * On touch end, determine whether to send a click event at once.
 *
 * @param {Event} event
 * @returns {boolean}
 */
FastClick.prototype.onTouchEnd = function(event) {
	'use strict';
	var forElement, trackingClickStart, targetTagName, scrollParent, touch, targetElement = this.targetElement;

	if (!this.trackingClick) {
		return true;
	}

	// Prevent phantom clicks on fast double-tap (issue #36)
	if ((event.timeStamp - this.lastClickTime) < this.tapDelay) {
		this.cancelNextClick = true;
		return true;
	}

	// Reset to prevent wrong click cancel on input (issue #156).
	this.cancelNextClick = false;

	this.lastClickTime = event.timeStamp;

	trackingClickStart = this.trackingClickStart;
	this.trackingClick = false;
	this.trackingClickStart = 0;

	// On some iOS devices, the targetElement supplied with the event is invalid if the layer
	// is performing a transition or scroll, and has to be re-detected manually. Note that
	// for this to function correctly, it must be called *after* the event target is checked!
	// See issue #57; also filed as rdar://13048589 .
	if (deviceIsIOSWithBadTarget) {
		touch = event.changedTouches[0];

		// In certain cases arguments of elementFromPoint can be negative, so prevent setting targetElement to null
		targetElement = document.elementFromPoint(touch.pageX - window.pageXOffset, touch.pageY - window.pageYOffset) || targetElement;
		targetElement.fastClickScrollParent = this.targetElement.fastClickScrollParent;
	}

	targetTagName = targetElement.tagName.toLowerCase();
	if (targetTagName === 'label') {
		forElement = this.findControl(targetElement);
		if (forElement) {
			this.focus(targetElement);
			if (deviceIsAndroid) {
				return false;
			}

			targetElement = forElement;
		}
	} else if (this.needsFocus(targetElement)) {

		// Case 1: If the touch started a while ago (best guess is 100ms based on tests for issue #36) then focus will be triggered anyway. Return early and unset the target element reference so that the subsequent click will be allowed through.
		// Case 2: Without this exception for input elements tapped when the document is contained in an iframe, then any inputted text won't be visible even though the value attribute is updated as the user types (issue #37).
		if ((event.timeStamp - trackingClickStart) > 100 || (deviceIsIOS && window.top !== window && targetTagName === 'input')) {
			this.targetElement = null;
			return false;
		}

		this.focus(targetElement);
		this.sendClick(targetElement, event);

		// Select elements need the event to go through on iOS 4, otherwise the selector menu won't open.
		// Also this breaks opening selects when VoiceOver is active on iOS6, iOS7 (and possibly others)
		if (!deviceIsIOS || targetTagName !== 'select') {
			this.targetElement = null;
			event.preventDefault();
		}

		return false;
	}

	if (deviceIsIOS && !deviceIsIOS4) {

		// Don't send a synthetic click event if the target element is contained within a parent layer that was scrolled
		// and this tap is being used to stop the scrolling (usually initiated by a fling - issue #42).
		scrollParent = targetElement.fastClickScrollParent;
		if (scrollParent && scrollParent.fastClickLastScrollTop !== scrollParent.scrollTop) {
			return true;
		}
	}

	// Prevent the actual click from going though - unless the target node is marked as requiring
	// real clicks or if it is in the whitelist in which case only non-programmatic clicks are permitted.
	if (!this.needsClick(targetElement)) {
		event.preventDefault();
		this.sendClick(targetElement, event);
	}

	return false;
};


/**
 * On touch cancel, stop tracking the click.
 *
 * @returns {void}
 */
FastClick.prototype.onTouchCancel = function() {
	'use strict';
	this.trackingClick = false;
	this.targetElement = null;
};


/**
 * Determine mouse events which should be permitted.
 *
 * @param {Event} event
 * @returns {boolean}
 */
FastClick.prototype.onMouse = function(event) {
	'use strict';

	// If a target element was never set (because a touch event was never fired) allow the event
	if (!this.targetElement) {
		return true;
	}

	if (event.forwardedTouchEvent) {
		return true;
	}

	// Programmatically generated events targeting a specific element should be permitted
	if (!event.cancelable) {
		return true;
	}

	// Derive and check the target element to see whether the mouse event needs to be permitted;
	// unless explicitly enabled, prevent non-touch click events from triggering actions,
	// to prevent ghost/doubleclicks.
	if (!this.needsClick(this.targetElement) || this.cancelNextClick) {

		// Prevent any user-added listeners declared on FastClick element from being fired.
		if (event.stopImmediatePropagation) {
			event.stopImmediatePropagation();
		} else {

			// Part of the hack for browsers that don't support Event#stopImmediatePropagation (e.g. Android 2)
			event.propagationStopped = true;
		}

		// Cancel the event
		event.stopPropagation();
		event.preventDefault();

		return false;
	}

	// If the mouse event is permitted, return true for the action to go through.
	return true;
};


/**
 * On actual clicks, determine whether this is a touch-generated click, a click action occurring
 * naturally after a delay after a touch (which needs to be cancelled to avoid duplication), or
 * an actual click which should be permitted.
 *
 * @param {Event} event
 * @returns {boolean}
 */
FastClick.prototype.onClick = function(event) {
	'use strict';
	var permitted;

	// It's possible for another FastClick-like library delivered with third-party code to fire a click event before FastClick does (issue #44). In that case, set the click-tracking flag back to false and return early. This will cause onTouchEnd to return early.
	if (this.trackingClick) {
		this.targetElement = null;
		this.trackingClick = false;
		return true;
	}

	// Very odd behaviour on iOS (issue #18): if a submit element is present inside a form and the user hits enter in the iOS simulator or clicks the Go button on the pop-up OS keyboard the a kind of 'fake' click event will be triggered with the submit-type input element as the target.
	if (event.target.type === 'submit' && event.detail === 0) {
		return true;
	}

	permitted = this.onMouse(event);

	// Only unset targetElement if the click is not permitted. This will ensure that the check for !targetElement in onMouse fails and the browser's click doesn't go through.
	if (!permitted) {
		this.targetElement = null;
	}

	// If clicks are permitted, return true for the action to go through.
	return permitted;
};


/**
 * Remove all FastClick's event listeners.
 *
 * @returns {void}
 */
FastClick.prototype.destroy = function() {
	'use strict';
	var layer = this.layer;

	if (deviceIsAndroid) {
		layer.removeEventListener('mouseover', this.onMouse, true);
		layer.removeEventListener('mousedown', this.onMouse, true);
		layer.removeEventListener('mouseup', this.onMouse, true);
	}

	layer.removeEventListener('click', this.onClick, true);
	layer.removeEventListener('touchstart', this.onTouchStart, false);
	layer.removeEventListener('touchmove', this.onTouchMove, false);
	layer.removeEventListener('touchend', this.onTouchEnd, false);
	layer.removeEventListener('touchcancel', this.onTouchCancel, false);
};


/**
 * Check whether FastClick is needed.
 *
 * @param {Element} layer The layer to listen on
 */
FastClick.notNeeded = function(layer) {
	'use strict';
	var metaViewport;
	var chromeVersion;

	// Devices that don't support touch don't need FastClick
	if (typeof window.ontouchstart === 'undefined') {
		return true;
	}

	// Chrome version - zero for other browsers
	chromeVersion = +(/Chrome\/([0-9]+)/.exec(navigator.userAgent) || [,0])[1];

	if (chromeVersion) {

		if (deviceIsAndroid) {
			metaViewport = document.querySelector('meta[name=viewport]');

			if (metaViewport) {
				// Chrome on Android with user-scalable="no" doesn't need FastClick (issue #89)
				if (metaViewport.content.indexOf('user-scalable=no') !== -1) {
					return true;
				}
				// Chrome 32 and above with width=device-width or less don't need FastClick
				if (chromeVersion > 31 && document.documentElement.scrollWidth <= window.outerWidth) {
					return true;
				}
			}

		// Chrome desktop doesn't need FastClick (issue #15)
		} else {
			return true;
		}
	}

	// IE10 with -ms-touch-action: none, which disables double-tap-to-zoom (issue #97)
	if (layer.style.msTouchAction === 'none') {
		return true;
	}

	return false;
};


/**
 * Factory method for creating a FastClick object
 *
 * @param {Element} layer The layer to listen on
 * @param {Object} options The options to override the defaults
 */
FastClick.attach = function(layer, options) {
	'use strict';
	return new FastClick(layer, options);
};


if (typeof define !== 'undefined' && define.amd) {

	// AMD. Register as an anonymous module.
	define('vendor/fastclick-1.0.1', [],function() {
		'use strict';
		return FastClick;
	});
} else if (typeof module !== 'undefined' && module.exports) {
	module.exports = FastClick.attach;
	module.exports.FastClick = FastClick;
} else {
	window.FastClick = FastClick;
}
;
define('require/apps/global_nav/analytics',['require','eb/analytics'],function(require) {
    'use strict';

    var Analytics = require('eb/analytics'),

        GlobalHeaderAnalytics = {
            helpAttempt: function(element, actionItem){
                // used to log when the user interacts with the dropdown that contains help options

                if (!actionItem.hasClass('gh-help')){
                    return; // return of not help menu item
                }

                if (element.type === 'click'){
                    // mouseStart or Touchstart is always triggered so don't
                    // log in the subsequent click event
                    return;
                }

                Analytics.trackEvent({
                    'action': 'helpAttempt',
                    'category': 'header',
                    'label': 'Help menu was viewed'
                });
            }
        };

    return GlobalHeaderAnalytics;
});

define('require/apps/global_nav/accessibleArrowKeys',['require','underscore'],function(require) {
    'use strict';

    var _ = require('underscore'),
        ARROW_KEYS = {
            RIGHT: 39,
            LEFT: 37
        },
        ARROW_KEYS_NEXT = [ARROW_KEYS.RIGHT],
        ARROW_KEYS_PREV = [ARROW_KEYS.LEFT];

    function handleKeyDown($parent, event) {
        // retrieve tabbables at time of keypress (rather than pre-caching)
        // because :tabbable takes visibility into account
        var $tabbable = $parent.find(':tabbable'),
            index = $tabbable.index(event.target),
            $nextElement;

        if (_(ARROW_KEYS_NEXT).contains(event.keyCode)) {
            $nextElement = index + 1 >= $tabbable.length
                ? $tabbable.eq(0)
                : $tabbable.eq(index + 1);
        } else if (_(ARROW_KEYS_PREV).contains(event.keyCode)) {
            $nextElement = $tabbable.eq(index - 1);
        }

        if ($nextElement) {
            $nextElement.focus();
            event.preventDefault();
        }
    };

    return {
        init: function($target) {
            $target.on(
                'keydown',
                _.bind(this.handleKeyDown, null, $target)
            );
        },

        handleKeyDown: handleKeyDown
    };
});

/**
* hoverIntent is similar to jQuery's built-in "hover" function except that
* instead of firing the onMouseOver event immediately, hoverIntent checks
* to see if the user's mouse has slowed down (beneath the sensitivity
* threshold) before firing the onMouseOver event.
* 
* hoverIntent r6 // 2011.02.26 // jQuery 1.5.1+
* <http://cherne.net/brian/resources/jquery.hoverIntent.html>
* 
* hoverIntent is currently available for use in all personal or commercial 
* projects under both MIT and GPL licenses. This means that you can choose 
* the license that best suits your project, and use it accordingly.
* 
* // basic usage (just like .hover) receives onMouseOver and onMouseOut functions
* $("ul li").hoverIntent( showNav , hideNav );
* 
* // advanced usage receives configuration object only
* $("ul li").hoverIntent({
*	sensitivity: 7, // number = sensitivity threshold (must be 1 or higher)
*	interval: 100,   // number = milliseconds of polling interval
*	over: showNav,  // function = onMouseOver callback (required)
*	timeout: 0,   // number = milliseconds delay before onMouseOut function call
*	out: hideNav    // function = onMouseOut callback (required)
* });
* 
* @param  f  onMouseOver function || An object with configuration options
* @param  g  onMouseOut function  || Nothing (use configuration options object)
* @author    Brian Cherne brian(at)cherne(dot)net
*/
(function($) {
	$.fn.hoverIntent = function(f,g) {
		// default configuration options
		var cfg = {
			sensitivity: 7,
			interval: 100,
			timeout: 0
		};
		// override configuration options with user supplied object
		cfg = $.extend(cfg, g ? { over: f, out: g } : f );

		// instantiate variables
		// cX, cY = current X and Y position of mouse, updated by mousemove event
		// pX, pY = previous X and Y position of mouse, set by mouseover and polling interval
		var cX, cY, pX, pY;

		// A private function for getting mouse position
		var track = function(ev) {
			cX = ev.pageX;
			cY = ev.pageY;
		};

		// A private function for comparing current and previous mouse position
		var compare = function(ev,ob) {
			ob.hoverIntent_t = clearTimeout(ob.hoverIntent_t);
			// compare mouse positions to see if they've crossed the threshold
			if ( ( Math.abs(pX-cX) + Math.abs(pY-cY) ) < cfg.sensitivity ) {
				$(ob).unbind("mousemove",track);
				// set hoverIntent state to true (so mouseOut can be called)
				ob.hoverIntent_s = 1;
				return cfg.over.apply(ob,[ev]);
			} else {
				// set previous coordinates for next time
				pX = cX; pY = cY;
				// use self-calling timeout, guarantees intervals are spaced out properly (avoids JavaScript timer bugs)
				ob.hoverIntent_t = setTimeout( function(){compare(ev, ob);} , cfg.interval );
			}
		};

		// A private function for delaying the mouseOut function
		var delay = function(ev,ob) {
			ob.hoverIntent_t = clearTimeout(ob.hoverIntent_t);
			ob.hoverIntent_s = 0;
			return cfg.out.apply(ob,[ev]);
		};

		// A private function for handling mouse 'hovering'
		var handleHover = function(e) {
			// copy objects to be passed into t (required for event object to be passed in IE)
			var ev = jQuery.extend({},e);
			var ob = this;

			// cancel hoverIntent timer if it exists
			if (ob.hoverIntent_t) { ob.hoverIntent_t = clearTimeout(ob.hoverIntent_t); }

			// if e.type == "mouseenter"
			if (e.type == "mouseenter") {
				// set "previous" X and Y position based on initial entry point
				pX = ev.pageX; pY = ev.pageY;
				// update "current" X and Y position based on mousemove
				$(ob).bind("mousemove",track);
				// start polling interval (self-calling timeout) to compare mouse coordinates over time
				if (ob.hoverIntent_s != 1) { ob.hoverIntent_t = setTimeout( function(){compare(ev,ob);} , cfg.interval );}

			// else e.type == "mouseleave"
			} else {
				// unbind expensive mousemove event
				$(ob).unbind("mousemove",track);
				// if hoverIntent state is true, then call the mouseOut function after the specified delay
				if (ob.hoverIntent_s == 1) { ob.hoverIntent_t = setTimeout( function(){delay(ev,ob);} , cfg.timeout );}
			}
		};

		// bind the function to the two event listeners
		return this.bind('mouseenter',handleHover).bind('mouseleave',handleHover);
	};
})(jQuery);
define("jquery.hoverintent", function(){});

define('require/apps/global_nav/global_nav',['require','underscore','jquery','vendor/fastclick-1.0.1','require/apps/global_nav/analytics','require/apps/global_nav/accessibleArrowKeys','jquery.hoverintent'],function(require) {
    'use strict';

    var _ = require('underscore'),
        $ = require('jquery'),
        FastClick = require('vendor/fastclick-1.0.1'),
        GlobalHeaderAnalytics = require('require/apps/global_nav/analytics'),
        accessibleArrowKeys = require('require/apps/global_nav/accessibleArrowKeys');

    require('jquery.hoverintent');

    return {
        init: function() {
            var actions,
                actionFunction,
                i;

            window._EB_GLOBAL_HEADER_INITED = true;
            _.bindAll(
                this,
                'showDropDown',
                'hideDropdown',
                'hideAllDropDowns',
                'toggleDropDown',
                'globalReset',
                'showMask',
                'hideMask',
                '_getElements',
                '_isDropDownElement',
                'preventClick',
                'bindHoverIntent',
                'unbindHoverIntent',
                'autocompleteRendered'
            );

            this.options = {
                // lock dropdown menus when action is clicked
                actionItemLock: true,
                hoverIntentOptions : {
                    over: this.showDropDown,
                    out: this.hideDropdown,
                    timeout: 300
                }
            };

            this.selectors = {
                globalHeader: '.js-global-header',
                actionContainer: '.js-gh-dd',
                actionItem: '.js-gh-action',
                actionActive: 'active',
                menuItem: '.js-gh-dd-menu',
                menuShowClass: 'js-gh-dd-menu--show',
                backgroundMask: 'show-menu-mask',
                menuLockClass: 'locked',
                dropdownLink: '.js-gh-dd-link'
            };

            this.touchSupport = (typeof Touch === 'object');

            actions = $(this.selectors.actionContainer);
            actionFunction = this.toggleDropDown;

            for (i = 0; i < actions.length; i++) {
                FastClick.attach(actions[i]);
            }

            if (!this.touchSupport){
                actionFunction = this.options.actionItemLock ? this.toggleDropDown : this.preventClick;
                this.bindHoverIntent($(this.selectors.actionContainer));
            }

            $('.global-mask').on('click touchstart', $.proxy(function (event) {
                // prevent user clicking through mask
                event.preventDefault();
                this.globalReset(event);
            },this));

            $(this.selectors.actionItem).on('click', actionFunction);
            $(this.selectors.actionItem).on('focus', actionFunction);
            $(this.selectors.dropdownLink).on('focus', this.hideAllDropDowns);

            // When the query input is focused there should not be any menu open in the page
            $('.js-query-input').on('focus', this.hideAllDropDowns);

            $('.js-search-query-area').bind('autocomplete:renderedView', this.autocompleteRendered);
            // global click to hide dropdowns
            $('body').on('click', this.globalReset);

            accessibleArrowKeys.init(
                $(this.selectors.globalHeader)
            );
        },

        showDropDown: function(event){
            var elements;

            // show the particular events dropdown
            // if other dropdowns then we hide them
            this.hideAllDropDowns();

            elements = this._getElements(event);

            GlobalHeaderAnalytics.helpAttempt(event, elements.actionItem);

            elements.actionItem
                .addClass(this.selectors.actionActive)
                .attr('aria-expanded', 'true');
            elements.menuItem
                .addClass(this.selectors.menuShowClass)
                .attr('aria-hidden', 'false')
                .attr('aria-expanded', 'true');
            this.showMask();
        },

        hideDropdown: function(event){
            // hide the particular events dropdown
            var elements = this._getElements(event);

            elements.actionItem
                .removeClass(this.selectors.actionActive)
                .attr('aria-expanded', 'false');
            elements.menuItem
                .removeClass(this.selectors.menuShowClass)
                .attr('aria-hidden', 'true')
                .attr('aria-expanded', 'false');
            this.hideMask();
        },

        hideAllDropDowns: function(){
            // hide all dropdowns
            var actionItems = $(this.selectors.actionItem);

            actionItems
                .removeClass(this.selectors.actionActive)
                .attr('aria-expanded', 'false');
            $(this.selectors.menuItem)
                .removeClass(this.selectors.menuShowClass)
                .attr('aria-hidden', 'true')
                .attr('aria-expanded', 'false');
            this.hideMask();

            if (this.options.actionItemLock && !this.touchSupport){
                this.bindHoverIntent($(this.selectors.actionContainer));
            }
            actionItems.removeClass(this.selectors.menuLockClass);
        },

        toggleDropDown: function(event){
            // toggle menu dropdowns
            var elements,
                $el;

            event.preventDefault();

            elements = this._getElements(event);

            if (elements.actionItem.hasClass(this.selectors.menuLockClass)) {
                // hide / unlock
                this.hideAllDropDowns();
            }
            else {
                // show / lock
                this.showDropDown(event);
                elements.actionItem.addClass(this.selectors.menuLockClass);

                if (this.options.actionItemLock && !this.touchSupport){
                    $el = $(event.currentTarget).closest(this.selectors.actionContainer);
                    // unbind the a particular dropdown
                    this.unbindHoverIntent($el);
                }
            }
        },

        globalReset: function(event){
            var target = event.target ? event.target : event.toElement;

            // if not active dropdown element or sub dropdown element then hide
            if (!this._isDropDownElement(target)){
                this.hideAllDropDowns();
            }
        },

        showMask: function(){
            $('body').addClass(this.selectors.backgroundMask);
        },

        hideMask: function(){
            $('body').removeClass(this.selectors.backgroundMask);
        },

        _getElements: function(event){
            // get the actionItem and menuItem based on the element clicked
            var $target = $(event.currentTarget),
                $actionItem = $target.children(this.selectors.actionItem),
                $menuItem = $target.children(this.selectors.menuItem);

            if (!$actionItem.length){
                $actionItem = $target;
                $menuItem = $target.next(this.selectors.menuItem);
            }
            // actionItem is the item that the user clicked
            // menuItem is the menu dropdown
            return {
                'actionItem': $actionItem,
                'menuItem': $menuItem
            };
        },

        _isDropDownElement: function(target){
            // find out if the active target is dropdown element
            if ($(target).closest('.js-gh-dd-menu, .js-gh-action').length) {
                return true;
            }
            return false;
        },

        preventClick: function(event){
            event.preventDefault();
        },

        bindHoverIntent: function($el){
            $el.hoverIntent(this.options.hoverIntentOptions);
        },

        unbindHoverIntent: function($el){
            $el.unbind('mouseenter').unbind('mouseleave');
            $el.removeProp('hoverIntent_t');
            $el.removeProp('hoverIntent_s');
        },

        autocompleteRendered: function(event, params){
            // uses search autocomplete trigger to add global header specific classes
            var globalHeader = $(this.selectors.globalHeader),
                $body = $('body');

            if (params.active){
                globalHeader.addClass('search-autocomplete-is-active');
                $body.addClass('show-search-mask');
            }
            else {
                globalHeader.removeClass('search-autocomplete-is-active');
                $body.removeClass('show-search-mask');
            }
        }
    };
});

// NOTE: ReviewBoard is required for any changes to this file.

(function($, EB, window, _, Backbone){
    'use strict';

    EB.ComponentFactory = function() {};

    _.extend(EB.ComponentFactory.prototype, {
        bindMethods: function(methods, context) {
            _.each(methods, function(method, key, list) {
                if (typeof method === 'function') {
                    list[key] = _.bind(method, context);
                };
            });
        },

        createComponent: function(options) {
            var component = _.extend({}, Backbone.Events),
                commands = _.clone(this.commands) || {},
                requests = _.clone(this.requests) || {},
                componentMethods = _.clone(this.componentMethods) || {},
                sandbox = _.clone(this.sandbox) || {};

            this.preCreate.call(component, options);

            component.sandbox = sandbox;
            component.commands = new Backbone.Wreqr.Commands();
            component.reqres = new Backbone.Wreqr.RequestResponse();

            // Rebind methods to the component to avoid scope leakage
            this.bindMethods(commands, component);
            this.bindMethods(requests, component);
            this.bindMethods(componentMethods, component);

            component = _.extend(component, componentMethods);

            component.commands.setHandlers(commands);
            component.reqres.setHandlers(requests);

            component.sandbox.execute = _.bind(component.commands.execute, component.commands);
            component.sandbox.request = _.bind(component.reqres.request, component.reqres);

            component.start = _.bind(this.start, component);
            component.stop = _.bind(this.stop, component);

            this.postCreate.call(component, options);

            return component;
        },

        create: function(options) {
            var component = this.createComponent(options);

            this.initialize.call(component, options);

            return component;
        },

        preCreate: function() {},
        postCreate: function() {},

        initialize: function() {},

        start: function() {},
        stop: function() {}
    });

    // Copy the `extend` function used by Backbone's classes
    EB.ComponentFactory.extend = Backbone.Model.extend;

    EB.ComponentFactory.create = function(options) {
        return new this().create(options);
    };

    if (typeof define === 'function') {
        define('eb/_backbone/component_factory', ['require','backbone_wreqr','backbone_advice'],function(require) {
            require('backbone_wreqr');
            var Advice = require('backbone_advice');

            Advice.addMixin(EB.ComponentFactory);

            return EB.ComponentFactory;
        });
    }

})(jQuery, EB, window, window._, window.Backbone);

define('require/entities/search_autocomplete_collection',['require','underscore','eventbrite','gettext'],function(require) {
    'use strict';

    var _ = require('underscore'),
        EB = require('eventbrite'),
        gettext = require('gettext');

    return EB.Backbone.Collection.extend({
        config: {
            length: 10,
            destinationAutocompleteUrl: '/api/v3/destination/search/autocomplete/'
        },

        initialize: function(options) {
            _.bindAll(
                this,
                '_parseAutocompleteItem',
                '_addAutocompleteItemToCollection',
                '_parseEventType',
                '_trimResponse',
                'getAutocompleteData'
            );

            this.term = options.term;
            this.url = this.config.destinationAutocompleteUrl;

            this.itemParseMap = {
                'event': this._parseEventType
            };
        },

        parse: function(response) {
            this.autocompleteData = [];


            if (response.event.length > 0) {
                this.currCategory = gettext('Events');
                this.currType = 'event';

                _.each(
                    this._trimResponse(response.event),
                    this._addAutocompleteItemToCollection
                );

                //add additional types to parse here
            }

        },

        _trimResponse: function(list, length) {
            length = length || this.config.length;

            return list.slice(0, length - 1);
        },

        _addAutocompleteItemToCollection: function(item, rank) {
            var itemData;

            itemData = this._parseAutocompleteItem(item, rank);

            if (itemData.label) {
                this.autocompleteData.push(itemData);
            }
        },

        _parseAutocompleteItem: function(item, rank) {
            return this.itemParseMap[this.currType](item, rank);
        },

        _parseEventType: function(item, rank) {
            var name = item.name,
                id = item.id,
                itemData = {
                    label: name,
                    value: name,
                    url: item.url,
                    source: 'autocomp',
                    sourceID: item['source_id'],
                    id: id,
                    rank: rank,
                    type: this.currType,
                    category: this.currCategory
                };

            return itemData;
        },

        _parseDirectoryItem: function(item, rank) {
            // XXX: remove old events_name
            var name = item['events_name'] || item.name,
                // XXX: remove old events_id
                id = item['events_id'] || item.id,
                itemData = {
                    label: name,
                    value: name,
                    url: item.url || item['resource_uri'],
                    source: 'autocomp',
                    sourceID: item['source_id'],
                    id: id,
                    rank: rank,
                    type: this.currType,
                    category: this.currCategory
                };

            return itemData;
        },

        getAutocompleteData: function() {
            return this.autocompleteData;
        }
    });

});

/**
 * Common functions for autocomplete
 * XXX: refactor and make component
 *
 */
define('require/utils/autocomplete_mixin',['require','jquery','jquery_autocomplete'],function(require) {
    'use strict';

    var $ = require('jquery');

    require('jquery_autocomplete');

    return function(autocomplete) {

        autocomplete._highlightText = function(resultString, matchString) {
            var matchWords,
                matcher;

            // escape any potential tags with empty string for no XSS
            resultString = $('<div>').text(resultString).html();

            matchWords = $.ui.autocomplete.escapeRegex(matchString).split('\\ ');
            matcher = new RegExp('('+ matchWords.join('|') +')', 'ig' );
            return resultString.replace(matcher, '<span class="text-body--significant">$1</span>');
        };

        autocomplete._renderItem = function(ul, item) {
            // allowing for highlighted text
            // only change here was to replace .text() with .html()
            return $('<li></li>')
                    .data('item.autocomplete', item)
                    .append($('<a></a>').html(item.label))
                    .appendTo(ul);
        };

        autocomplete._populateAutocomplete = function() {
            // force the autocomplete to fill, often used with .focus()

            $(this).autocomplete('search');
        };
    };
});

define('require/components/search_autocomplete/search_input_view',['require','underscore','jquery','backbone','eventbrite','eb/analytics','require/entities/search_autocomplete_collection','require/utils/autocomplete_mixin','gettext','eb/util','jquery_autocomplete'],function(require) {
    'use strict';

    var _ = require('underscore'),
        $ = require('jquery'),
        Backbone = require('backbone'),
        EB = require('eventbrite'),
        Analytics = require('eb/analytics'),
        AutocompleteQuerySuggestions = require('require/entities/search_autocomplete_collection'),
        AutocompleteFunctions = require('require/utils/autocomplete_mixin'),
        gettext = require('gettext');

    require('eb/util');
    require('jquery_autocomplete');

    return Backbone.View.extend({

        events: {
            'focus .js-search-query-input': 'handleSearchQueryFocus',
            'blur .js-search-query-input': 'handleSearchQueryBlur',
            'click .js-header-search-icon': 'handleHeaderSearchIconClick',
            'keydown .js-search-query-input': 'handleQueryKeydown',
            'paste .js-search-query-input': 'handleQueryPaste',
            'click .js-search-submit': 'handleFormSubmit'
        },

        config: {
            'searchSuggestionsDelay': 400,
            // Place id for United States as final fallback
            'defaultPlaceId': 85633793
        },

        initialize: function(options) {
            _.bindAll(
                this,
                'handleSearchQueryFocus',
                'handleSearchQueryBlur',
                'getSuggestions',
                'handleSelectEvent',
                'renderAutocompleteMenu',
                'hideAutocomplete',
                'handleQueryKeydown',
                'triggerQueryInputFocus',
                'setSearchInitStatus',
                'getPlaceIdFromCookie'
            );

            this.sandbox = options.sandbox;
            this.options = options;

            new AutocompleteFunctions(this);

            this.$searchQueryArea = this.$('.js-search-query-area');
            this.$searchQueryInput = this.$('.js-search-query-input');
            this.$searchQueryIcon = this.$('.js-search-query-icon');

            if (!this.$searchQueryInput.length) {
                // if no search element present return
                return;
            }

            // Create collections for autocomplete
            this.suggestions = new AutocompleteQuerySuggestions({});
            this.suggestions.fetch = _.debounce(this.suggestions.fetch, this.config.searchSuggestionsDelay);

            this.sandbox.searchQuery = this.$searchQueryInput.val();

            this.initializeEventsAutocomplete();

            this.setSearchInitStatus();
        },

        getPlaceIdFromCookie: function() {
            var locationFromCookie,
                placeId;

            if (self.placeId) {
                return self.placeId;
            }

            locationFromCookie = $.cookie('location');

            if (locationFromCookie && Object.keys(locationFromCookie).indexOf('place_id') > -1) {
                placeId = locationFromCookie['place_id'];
            }

            self.placeId = placeId;
            return placeId;
        },

        setSearchInitStatus: function() {
            this.searchStarted = false;
            this.resultsDisplayed = false;
        },

        initializeEventsAutocomplete: function() {
            this.$searchQueryInput.autocomplete({
                appendTo: this.$el,
                minLength: 0,
                select: this.handleSelectEvent,
                source: this.getSuggestions,
                position: {
                    my: 'left top',
                    at: 'left bottom',
                    of: this.$el
                },
                delay: 100
            }).focus(
                this._populateAutocomplete
            ).data('autocomplete')._renderMenu = this.renderAutocompleteMenu;

        },
        renderDefaultView: function() {
            this.$searchQueryArea.addClass('show-large');
            this.$el.removeClass('is-active sill sill--with-borders');
            this.$searchQueryArea.trigger('autocomplete:renderedView', {active: false});
            this.$searchQueryInput[0].blur();
        },
        renderQueryInputView: function() {
            this.$searchQueryArea.removeClass('show-large');
            this.$el.addClass('is-active sill sill--with-borders');
            this.$searchQueryArea.trigger('autocomplete:renderedView', {active: true});
        },
        renderAutocompleteMenu: function(ul, items) {
            var self = this,
                currentCategory = '';

            ul.removeClass('ui-menu ui-widget ui-widget-content ui-corner-all');
            ul.addClass('list sill sill--with-borders search-query-autocomplete');

            // Make this into a Marionette view?
            $.each(items, function(index, item) {
                // TODO: categories will be added in a future version, right now we only show events
                if (item.category !== currentCategory && item.category) {
                    ul.append(EB.util.h(
                        '<p class="list--divider">{{a}}</p>',
                        item.category
                    ));
                    currentCategory = item.category;
                }
                ul.append(self._renderItem(ul, item));
            });

            this.trackResultsDisplayed(items);
        },

        // sometimes the autocomplete isn't correctly hidden when the input loses focus.
        // Force it to be hidden by calling this function
        hideAutocomplete: function() {
            // sometimes disable just isn't enough for IE
            this.$searchQueryInput.trigger('blur');
            this.$searchQueryInput.autocomplete('disable');
        },

        handleHeaderSearchIconClick: function(e) {
            e.preventDefault();
            if (this.sandbox.request('autocomplete:isInteracting')) {
                this.sandbox.execute('autocomplete:interaction:stop');
            } else {
                this.sandbox.execute('autocomplete:interaction:start');
            }
        },
        trackSearchStart: function() {
            if (!this.searchStarted) {
                Analytics.analyticsAction('SearchStartAction', {}, {});
                this.searchStarted = true;
            }
        },
        trackResultsDisplayed: function() {
            if (this.searchStarted && !this.resultsDisplayed) {
                Analytics.analyticsAction('AutocompleteResultViewAction', {}, {});
                this.resultsDisplayed = true;
            }
        },
        trackSearchStop: function() {
            if (this.resultsDisplayed) {
                Analytics.analyticsAction(
                    'SearchAbandonedAction',
                    {
                        'search_terms': this.$searchQueryInput.val()
                    },
                    {}
                );
            }
            this.setSearchInitStatus();
        },
        handleSearchQueryFocus: function() {
            this.sandbox.execute('autocomplete:interaction:start');
            this.$searchQueryArea.addClass('has-focus');
            this.$searchQueryIcon.addClass('is-focus');
            this.$searchQueryInput.autocomplete('enable');
        },
        handleSearchQueryBlur: function() {
            this.$searchQueryArea.removeClass('has-focus');
            this.$searchQueryIcon.removeClass('is-focus');
        },
        handleQueryKeydown: function() {
            this.handleQueryInput();
            this.trackSearchStart();
        },
        handleQueryPaste: function() {
            this.$searchQueryInput.trigger('keydown');
        },
        handleQueryInput: function() {
            // the actual autocomplete is already being handled by jquery autocomplete
            this.sandbox.searchQuery = this.$searchQueryInput.val();
        },
        handleSelectEvent: function(e, ui) {
            e.preventDefault();
            this.sandbox.execute('autocomplete:query:suggestionSelected', ui.item);
        },
        handleFormSubmit: function() {
            Analytics.analyticsAction(
                'SearchEventsClickAction',
                {
                    'source': 'globalheader'
                },
                {}
            );
            Analytics.trackEventFromLink({
                'category': 'header',
                'action': 'RunSearch',
                'label': this.sandbox.searchQuery
            });
            this.setSearchInitStatus();
        },
        triggerQueryInputFocus: function() {
            this.$searchQueryInput.trigger('focus');
        },

        _getPlaceDataForAutocomplete: function() {
            var placeId,
                lat,
                lng;

            placeId = this.getPlaceIdFromCookie();

            if (placeId) {
                return {placeId: placeId};
            }

            if (this.options.userLocationFromIp) {
                lat = this.options.userLocationFromIp.latitude;
                lng = this.options.userLocationFromIp.longitude;
            }

            if (lat && lng) {
                return {
                    lat: lat,
                    lng: lng
                };
            }

            return {placeId: this.config.defaultPlaceId};
        },
        getSuggestions: function(request, response) {
            var data = {
                    'q': this.$searchQueryInput.val(),
                    'completion_types': 'event,query'
                },
                placeData,
                self = this;

            //add place_id or lat/lng to data here
            placeData = this._getPlaceDataForAutocomplete();

            if (placeData.placeId) {
                data.place_id = placeData.placeId;
            } else if (placeData.lat && placeData.lng) {
                data.latitude = placeData.lat;
                data.longitude = placeData.lng;
            }

            this.suggestions.term = this.$searchQueryInput.val();

            if (this.suggestions.term.length < 2) {
                response([{
                        type: 'Category',
                        category: gettext('Categories'),
                        label: gettext('Arts'),
                        source: 'autocomp',
                        url: '/d/local/arts--events/',
                        value: 'Arts'
                    },{
                        type: 'Category',
                        category: gettext('Categories'),
                        label: gettext('Business'),
                        source: 'autocomp',
                        url: '/d/local/business--events/',
                        value: 'Business'
                    },{
                        type: 'Category',
                        category: gettext('Categories'),
                        label: gettext('Charity & Causes'),
                        source: 'autocomp',
                        url: '/d/local/charity-and-causes--events/',
                        value: 'Charity & Causes'
                    },{
                        type: 'Category',
                        category: gettext('Categories'),
                        label: gettext('Community'),
                        source: 'autocomp',
                        url: '/d/local/community--events/',
                        value: 'Community'
                    },{
                        type: 'Category',
                        category: gettext('Categories'),
                        label: gettext('Film & Media'),
                        source: 'autocomp',
                        url: '/d/local/film-and-media--events/',
                        value: 'Film & Media'
                    },{
                        type: 'Category',
                        category: gettext('Categories'),
                        label: gettext('Food & Drink'),
                        source: 'autocomp',
                        url: '/d/local/food-and-drink--events/',
                        value: 'Food & Drink'
                    },{
                        type: 'Category',
                        category: gettext('Categories'),
                        label: gettext('Music'),
                        source: 'autocomp',
                        url: '/d/local/music--events/',
                        value: 'Music'
                    },{
                        type: 'Category',
                        category: gettext('Categories'),
                        label: gettext('All Categories'),
                        source: 'autocomp',
                        url: '/d/local/all-events/',
                        value: 'All Categories'
                    }
                ]);
            } else {
                this.suggestions.fetch({
                    reset: true,
                    data: data,
                    success: function() {
                        response(self.suggestions.getAutocompleteData());
                    }
                });
            }
        }

    });

});

define('require/components/search_autocomplete/component',['require','jquery','backbone','mediatorjs','eb/_backbone/component_factory','require/components/search_autocomplete/search_input_view','eb/analytics','gettext'],function(require) {
    'use strict';

    var $ = require('jquery'),
        Backbone = require('backbone'),
        mediator = require('mediatorjs'),
        ComponentFactory = require('eb/_backbone/component_factory'),
        SearchInputView = require('require/components/search_autocomplete/search_input_view'),
        Analytics = require('eb/analytics'),
        gettext = require('gettext');

    // gettext currently is not getting included in mobile custom pages because it is not necessary
    // stub out gettext if it doesn't exist
    if (!gettext) {
        gettext = function(text) {
            return text;
        };
    }

    jQuery.fn.extend({
        propAttr: $.fn.prop || $.fn.attr
    });

    return ComponentFactory.extend({

        initialize: function(options) {
            this.initializeSearch(options);
            this.initializeViews(options);
        },

        componentMethods: {
            initializeSearch: function() {
                this.isSearchPage = false;
                this.isInteracting = false;
                this.sandbox.searchQuery = '';
            },

            initializeViews: function(options) {
                options.sandbox = this.sandbox;

                this.searchInputView = new SearchInputView(options);
                this.searchInputView.render();
            },

            // track every action the user makes while interacting with the autocomplete.
            // If they stop interacting, this eventHandler should be removed
            eventHandler: function(e) {
                if (this.searchInputView.$el.find(e.target).length < 1){

                    // when you click on the search icon on
                    // jquery mobile pages the first e.target is a jquery mobile page
                    // with class ui-page. This is triggered even if you are still interacting with
                    if (!$(e.target).hasClass('ui-page')) {
                        this.sandbox.execute('autocomplete:interaction:stop');
                    }
                }
            }
        },

        commands: {
            // The basic interaction states. A user is either not interacting (default),
            // entering a query, or entering a location

            // the user started interacting
            'autocomplete:interaction:start': function() {
                $('.js-header-search-autocomplete').removeClass('is-init');
                if (!this.isInteracting) {
                    $('body').on('click focusin touchstart', this.eventHandler);
                    this.isInteracting = true;
                    this.sandbox.execute('autocomplete:navigate:query');
                }
            },
            // The user stopped interacting
            'autocomplete:interaction:stop': function() {
                $('.js-header-search-autocomplete').addClass('is-init');
                this.searchInputView.renderDefaultView();
                this.searchInputView.hideAutocomplete();
                $('body').off('click focusin touchstart', this.eventHandler);
                this.isInteracting = false;
                this.searchInputView.trackSearchStop();
            },


            // s user is interacting with the query input
            'autocomplete:navigate:query': function() {
                this.searchInputView.renderQueryInputView();
                this.searchInputView.triggerQueryInputFocus();
            },
            // the user is interacting with the location input
            'autocomplete:navigate:location': function() {
                this.searchInputView.hideAutocomplete();
                this.searchInputView.renderQueryInputView();
            },

            // An autocompleted suggestion has been selected, redirect to the suggestion URL

            'autocomplete:query:suggestionSelected': function(uiItem) {
                var properties;

                Analytics.trackInternalNavigationAction(
                    'AutocompleteResultClickAction',
                    {
                        'event_id': uiItem.id,
                        'autocomp_suggestion': uiItem.value,
                        'autocomp_type': uiItem.type,
                        'rank': uiItem.rank,
                        'source_id': uiItem.sourceID,
                        'search_location': 'Current Location',
                        'search_terms': this.searchInputView.$searchQueryInput.val()
                    },
                    {}
                );
                if (uiItem.type === 'Event' || uiItem.type === 'Top Event') {
                    Analytics.trackEventFromLink({
                        'action': 'ViewEventAutocomplete',
                        'category': 'header',
                        'label': uiItem.id
                    });
                }
                properties = {
                    'Search Query': this.sandbox.searchQuery,
                    'Location': 'Current Location',
                    'Rank': uiItem.rank,
                    'Number of Results': this.searchInputView.$el.find('li').length
                };
                //pop deals with the fact that .category has form
                //e.g. 'San Francisco Events'
                properties['Result Type'] = uiItem.category.split(' ').pop();
                window.location = uiItem.url;
            }
        },

        requests: {
            'autocomplete:query': function() {
                return this.sandbox.searchQuery;
            },
            'autocomplete:isInteracting': function() {
                return this.isInteracting;
            }
        }
    });
});

require([
    'eb/require_base_config',
    'require/apps/global_nav/global_nav',
    'require/components/search_autocomplete/component',
    'eb-styleguide/js/ui_components/component_library',
    'jquery',
    'mediatorjs'
], function(
    RequireConfig,
    GlobalNav,
    SearchAutocompleteComponent,
    ComponentLibrary,
    $,
    mediatorjs
) {
    'use strict';

    var config = mediatorjs.get('globalHeader'),
        autoCompleteData = {
            el: '.js-header-search-autocomplete'
        };

    GlobalNav.init();

    $(function() {
        ComponentLibrary.wireAll();
    });

    if (config) {
        autoCompleteData = config;
        config.el = '.js-header-search-autocomplete';
    }

    window.searchAutocomplete = SearchAutocompleteComponent.create(autoCompleteData);
});

define("require/apps/global_nav/app", function(){});

